/**
 * shopCache — zentrale Cache-Utilities für alle öffentlichen Shop-APIs.
 *
 * Ziel: eliminiert sichtbare Content-Nachlade-Bewegungen ("Layout-Shift"
 * beim ersten Paint) und beschleunigt den 2nd-Load spürbar. Alle Shop-Seiten
 * lesen den Cache SYNCHRON beim Mount (via useState-Initializer), holen die
 * frischen Daten dann parallel im Hintergrund via `axios` und aktualisieren
 * State + Cache in einem Rutsch (Stale-While-Revalidate-Pattern).
 *
 * Storage-Layout (ein localStorage-Slot pro Key, damit Größen kontrolliert bleiben):
 *   - hs_cache_settings          → GET /api/shop/settings
 *   - hs_cache_homepage          → GET /api/shop/homepage-config
 *   - hs_cache_categories        → GET /api/shop/categories
 *   - hs_cache_nav_pages         → GET /api/shop/pages/nav
 *   - hs_cache_active_campaigns  → GET /api/shop/active-campaigns
 *   - hs_header_cache            → Legacy-Slot für Frosted-Attribut (weiterhin
 *                                   vom Inline-Script in public/index.html gelesen).
 *
 * TTL: default 24 h. Ältere Einträge werden verworfen, damit User bei einem
 * verpassten Update nicht ewig mit alten Daten sitzen.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

function nowMs() { return Date.now(); }

/** Liest einen Cache-Eintrag; verwirft abgelaufene Werte. */
export function readShopCache(key, ttlMs = DEFAULT_TTL_MS) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry !== 'object') return null;
    if (typeof entry.t !== 'number') return null;
    if (nowMs() - entry.t > ttlMs) return null;
    return entry.v ?? null;
  } catch { return null; }
}

/** Schreibt einen Cache-Eintrag mit Timestamp. */
export function writeShopCache(key, value) {
  try {
    const raw = JSON.stringify({ t: nowMs(), v: value });
    // Grober Overflow-Schutz: sehr große Payloads (>200KB) nicht cachen.
    if (raw.length > 200 * 1024) return;
    localStorage.setItem(key, raw);
  } catch (_) { /* Quota / private-mode → still */ }
}

/**
 * useShopApi — Stale-While-Revalidate-Hook für einen öffentlichen Shop-Endpoint.
 * Gibt den Cache-Wert SOFORT beim Mount zurück (falls vorhanden) und holt
 * dann im Hintergrund die frischen Daten. Setter wird nur aufgerufen, wenn
 * sich der Payload wirklich unterscheidet (JSON-Vergleich).
 *
 * @param {string} cacheKey  eindeutiger localStorage-Key
 * @param {string} url       vollständige URL (inkl. REACT_APP_BACKEND_URL)
 * @param {object} opts      { enabled?: boolean, ttlMs?: number, onData?: (data)=>void }
 * @returns {[data, {loading, refresh}]}
 */
export function useShopApi(cacheKey, url, opts = {}) {
  const { enabled = true, ttlMs = DEFAULT_TTL_MS, onData } = opts;
  const [data, setData] = useState(() => (enabled ? readShopCache(cacheKey, ttlMs) : null));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !url) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get(url)
      .then((res) => {
        if (cancelled) return;
        const fresh = res.data;
        setData((prev) => {
          try {
            if (JSON.stringify(prev) === JSON.stringify(fresh)) return prev;
          } catch (_) { /* fallthrough */ }
          return fresh;
        });
        writeShopCache(cacheKey, fresh);
        if (typeof onData === 'function') onData(fresh);
      })
      .catch(() => { /* Fehler wird still geschluckt – Cache-Wert bleibt sichtbar */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cacheKey, url, enabled]);

  return [data, { loading, refresh: () => { /* placeholder for external trigger */ } }];
}

/* --- Vordefinierte Cache-Keys (damit sich niemand vertippt) ---------------- */
export const CK = {
  SETTINGS: 'hs_cache_settings',
  HOMEPAGE: 'hs_cache_homepage',
  CATEGORIES: 'hs_cache_categories',
  NAV_PAGES: 'hs_cache_nav_pages',
  ACTIVE_CAMPAIGNS: 'hs_cache_active_campaigns',
};

/**
 * Juni 2026 · Löscht alle Shop-Caches (localStorage `hs_cache_*` + Legacy-
 * Header-Slot). Wird nach dem Speichern im Startseiteneditor aufgerufen,
 * damit Änderungen sofort sichtbar sind.
 */
export function clearShopCache() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('hs_cache_') || k === 'hs_header_cache')
      .forEach((k) => localStorage.removeItem(k));
  } catch (_) { /* private mode */ }
}

/**
 * Feb 2026 · Sub-Shop-scope Homepage-Config Cache-Key.
 * Vermeidet den Header-Flash beim Wechsel Hauptshop → Sub-Shop: jeder
 * Sub-Shop bekommt einen eigenen Cache-Slot pro Slug, damit beim erneuten
 * Aufruf sofort die richtige Header-Variante & Config gerendert wird.
 */
export function subShopHomepageKey(slug) {
  const clean = String(slug || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
  return `hs_cache_homepage__sub_${clean}`;
}
